/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";
import DomainListPanels from "../components/DomainListPanels";
import TrackingLinkPanel from "../components/TrackingLinkPanel";
import marketingPartnerApi from "../apis/mp-configs";
import useApi from "../hooks/useApi";
import { modelExDomainData, modelRunnableOfferData, modelSharedOfferData } from "../apis/modelData";
import { UserContext } from "../context/user";
import { header } from "./header";

const PixelsAndDomains = () => {
  // Context & Params
  const { token } = useParams();
  const { state: user } = useContext(UserContext);
  const encryptedToken = token || user.token || (new URLSearchParams(window.location.search)).get('efo_mp');

  // UI State
  const [loaded, setLoaded] = useState(false);
  const [retry, setRetry] = useState(false);
  const [dataRefreshTrigger, setRefreshTrigger] = useState("");

  // Data State
  const [exDomains, setExDomains] = useState(null);
  const [sharedDomains, setSharedDomains] = useState(null);
  const [offerPrefixes, setOfferPrefixes] = useState(null);
  const [runOffers, setRunOffers] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [mpBrands, setMpBrands] = useState(null);

  // Bundle data for children
  const data = { mpBrands, exDomains, sharedDomains, offerPrefixes, offerData, runOffers };

  /* Api Hooks */
  const { data: offersListData, request: getOffersList } = useApi(marketingPartnerApi.getOffersList);
  const { data: mpRunnableOffersData, request: mpRunnableOffers } = useApi(marketingPartnerApi.getMarketingPartnerRunnableOffers);
  const { data: mpOfferConfigsData, request: mpOfferConfigs } = useApi(marketingPartnerApi.getMarketingPartnerConfigs);
  const { data: mpBrandsData, request: mpGetBrands } = useApi(marketingPartnerApi.getMarketingPartnerBrands);
  const { data: mpSharedOffersData, request: mpGetSharedOffers } = useApi(marketingPartnerApi.getMarketingPartnerSharedOffers);

  // Notifications Logic
  const [showAlert, setShowAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState(false);
  const [alertStyle, setAlertStyle] = useState(false);

  const onShowNotification = (style = "info", message) => {
    setShowAlert(true);
    setAlertMsg(message);
    setAlertStyle(style);
  };

  const Notifications = () => {
    if (!showAlert) return null;
    setTimeout(() => setShowAlert(false), 7000);
    return (
        <Alert variant={alertStyle} onClose={() => setShowAlert(false)} dismissible>
          <p dangerouslySetInnerHTML={{ __html: alertMsg }} />
        </Alert>
    );
  };

  // Actions
  const refreshData = () => setRefreshTrigger(new Date().getTime());
  const onRetry = () => window.location.reload();

  // 1. Initial Load & Timer
  useEffect(() => {
    const interval = 30000;
    const timer = setTimeout(() => setRetry(true), interval);
    return () => clearTimeout(timer);
  }, []);

  // 2. Data Fetching Trigger
  useEffect(() => {
    if (encryptedToken) {
      mpGetBrands(encryptedToken);
      mpOfferConfigs(encryptedToken);
      getOffersList();
      mpRunnableOffers(encryptedToken, { page: 1, pageSize: 1000 });
      mpGetSharedOffers(encryptedToken);
    }
  }, [dataRefreshTrigger, encryptedToken]);

  // 3. ATOMIC DATA MODELING
  useEffect(() => {
    const hasRunnable = Object.keys(mpRunnableOffersData).length > 0;
    const hasOffers = Object.keys(offersListData).length > 0;
    const hasConfigs = Object.keys(mpOfferConfigsData).length > 0;
    const hasBrands = Object.keys(mpBrandsData).length > 0;

    if (hasRunnable && hasOffers && hasConfigs && hasBrands) {
      const rawOffers = mpRunnableOffersData.result.offers || [];
      const uniqueOffers = Array.from(
          new Map(rawOffers.map(item => [item['networkOfferId'], item])).values()
      );

      // --- MODELING WITH CLEAN DATA ---
      // Exclusive Domains & Native Offer Data
      const modeledExData = modelExDomainData(
          mpOfferConfigsData,
          mpBrandsData.result,
          true,
          uniqueOffers
      );

      // Offer Prefixes (Internal GiddyUp Configs mapped to unique runnable offers)
      const modeledPrefixes = modelRunnableOfferData(
          uniqueOffers,
          offersListData.result.offers
      );

      // Update All States Atomically
      setRunOffers(uniqueOffers);
      setExDomains(modeledExData.collated);
      setOfferData(modeledExData.native);
      setMpBrands(mpBrandsData.result);
      setOfferPrefixes(modeledPrefixes);
    }
  }, [mpRunnableOffersData, offersListData, mpOfferConfigsData, mpBrandsData]);

  // 4. Shared Domains Modeling (Separate because it doesn't strictly depend on uniqueOffers)
  useEffect(() => {
    if (Object.keys(mpOfferConfigsData).length && Object.keys(mpSharedOffersData).length) {
      setSharedDomains(modelSharedOfferData(mpSharedOffersData.result.offers, mpOfferConfigsData.result));
    }
  }, [mpOfferConfigsData, mpSharedOffersData]);

  // 5. Final Loaded State Trigger
  useEffect(() => {
    if (exDomains !== null && runOffers !== null && sharedDomains !== null) {
      setLoaded(true);
    }
  }, [exDomains, runOffers, sharedDomains]);

  // RENDER LOGIC
  if (loaded) {
    return (
        <>
          <Notifications />
          <div className="container-fluid dashboard">
            <div className="row dashboard-header">
              <div className="col-xs-12 col-sm-8 col-md-8" dangerouslySetInnerHTML={{ __html: header }} />
              <div className="col-xs-12 col-sm-4 col-md-4">
                <a href="https://helpcenter.giddyup.io/en/collections/10701434-premium-pixels-exclusive-domains" target="_blank" rel="noreferrer" className="help-link">?</a>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-8 col-md-8">
                <DomainListPanels showNotification={onShowNotification} refreshData={refreshData} data={data} />
              </div>
              <div className="col-sm-4 col-md-4">
                <TrackingLinkPanel data={data} />
              </div>
            </div>
          </div>
        </>
    );
  }

  if (encryptedToken) {
    return (
        <div className={retry ? "loading-retry" : "loading"}>
          {retry ? (
              <>
                <p><i className="fa fa-exclamation-triangle" /> Could not load the app.</p>
                <Button variant="action" onClick={onRetry}>Retry?</Button>
              </>
          ) : (
              <div className="spinner" />
          )}
        </div>
    );
  }

  return <div className="token-error">Token Error</div>;
};

export default PixelsAndDomains;